import { FormEvent, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from 'shared/hooks/useAuth';
import { ROUTES } from 'shared/constants/routes';
import { UserRole } from 'shared/types/user';
import { PlayerSportLevel } from 'modules/jogador/profile/content';
import { AUTOPILOT_PARAM } from 'shared/demo/autopilot';

type SignupStep = 'info' | 'sports';

export const SIGNUP_CITIES = ['Fortaleza-CE', 'Caucaia-CE', 'Eusébio-CE', 'Aquiraz-CE', 'Maracanaú-CE'];

// Máscara de celular brasileiro enquanto digita: (85) 99999-9999.
export function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 2) return digits.length ? `(${digits}` : '';
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

export function useSignupForm() {
  const [step, setStep] = useState<SignupStep>('info');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhoneRaw] = useState('');
  const [city, setCity] = useState(SIGNUP_CITIES[0]);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [searchParams] = useSearchParams();
  // ?perfil=arena chega com "Sou dono de arena" já marcado (botões "Cadastrar minha arena").
  const [role, setRole] = useState<UserRole>(searchParams.get('perfil') === 'arena' ? 'arena' : 'jogador');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [selectedSports, setSelectedSports] = useState<string[]>([]);
  const [levelsBySport, setLevelsBySport] = useState<Record<string, PlayerSportLevel['level']>>({});

  const { register } = useAuth();
  const navigate = useNavigate();

  const setPhone = (value: string) => setPhoneRaw(formatPhone(value));

  function toggleSport(sport: string) {
    setSelectedSports((prev) => {
      if (prev.includes(sport)) return prev.filter((item) => item !== sport);
      return [...prev, sport];
    });
    setLevelsBySport((prev) => (prev[sport] ? prev : { ...prev, [sport]: 'Iniciante' }));
  }

  function setSportLevel(sport: string, level: PlayerSportLevel['level']) {
    setLevelsBySport((prev) => ({ ...prev, [sport]: level }));
  }

  // `autopilot`: a simulação automática segue direto para o onboarding também em modo automático.
  async function doRegister(autopilot = false) {
    setError(null);
    setIsSubmitting(true);

    try {
      const user = await register({ name, email, password, role, phone, city });
      const next = user.role === 'arena' ? ROUTES.ARENA_ONBOARDING : ROUTES.HOME;
      navigate(autopilot && user.role === 'arena' ? `${next}?${AUTOPILOT_PARAM}=1` : next, { replace: true });
    } catch {
      setError('Não foi possível concluir o cadastro. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  }

  // Jogadores passam por uma segunda etapa (esportes praticados + nível, seção 11 do
  // PRD); dono de arena segue direto — os dados da arena são coletados no onboarding.
  async function handleInfoSubmit(event: FormEvent) {
    event.preventDefault();
    if (role === 'jogador') {
      setStep('sports');
      return;
    }
    await doRegister();
  }

  async function handleSportsSubmit(event: FormEvent) {
    event.preventDefault();
    await doRegister();
  }

  function backToInfo() {
    setStep('info');
  }

  return {
    step,
    name,
    setName,
    email,
    setEmail,
    password,
    setPassword,
    phone,
    setPhone,
    city,
    setCity,
    acceptedTerms,
    setAcceptedTerms,
    role,
    setRole,
    error,
    isSubmitting,
    selectedSports,
    levelsBySport,
    toggleSport,
    setSportLevel,
    handleInfoSubmit,
    handleSportsSubmit,
    backToInfo,
    doRegister,
  };
}
